import React from 'react';
import { Box, CircularProgress } from '@mui/material';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';

/** מצב פידבק אינליין לשמירה — לשימוש חוזר ברחבי האפליקציה */
export type SaveActionStatus = 'idle' | 'loading' | 'success' | 'error';

export type InlineSaveIndicatorProps = {
  status: Exclude<SaveActionStatus, 'idle'>;
  /** גודל ויזואלי (ברירת מחדל ~20px) */
  sizePx?: number;
};

/**
 * חיווי שמירה קטן ליד / במקום אייקון: טבעת כחולה (אינדטרמיניסטית, בסגנון Material),
 * ואז V ירוק או X אדום.
 */
export function InlineSaveIndicator({ status, sizePx = 20 }: InlineSaveIndicatorProps) {
  const s = sizePx;

  if (status === 'loading') {
    return (
      <Box
        role="status"
        aria-busy="true"
        aria-label="Saving"
        sx={{
          width: s,
          height: s,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <CircularProgress
          size={s}
          thickness={4}
          color="primary"
          variant="indeterminate"
          sx={{
            // קרוב ל־Material / Google: משך ועקומת האצה
            animationDuration: '1.4s',
            animationTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
            '& .MuiCircularProgress-circle': {
              strokeLinecap: 'round',
            },
          }}
        />
      </Box>
    );
  }

  if (status === 'success') {
    return (
      <CheckRoundedIcon
        sx={{ fontSize: s, color: 'success.main', flexShrink: 0 }}
        aria-hidden
        role="img"
      />
    );
  }

  return (
    <CloseRoundedIcon
      sx={{ fontSize: s, color: 'error.main', flexShrink: 0 }}
      aria-hidden
      role="img"
    />
  );
}

/**
 * מתזמן חזרה ל־idle אחרי הצגת success/error (למניעת דליפות טיימרים).
 */
export function useSaveFeedbackIdleScheduler() {
  const timersRef = React.useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  React.useEffect(
    () => () => {
      timersRef.current.forEach(clearTimeout);
      timersRef.current.clear();
    },
    [],
  );

  const scheduleIdle = React.useCallback((key: string, onIdle: () => void, delayMs = 1600) => {
    const map = timersRef.current;
    const prev = map.get(key);
    if (prev) clearTimeout(prev);
    const t = setTimeout(() => {
      map.delete(key);
      onIdle();
    }, delayMs);
    map.set(key, t);
  }, []);

  return scheduleIdle;
}
